import { FC, useMemo } from "react";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { EmptyState } from "@design-system/EmptyState/EmptyState";
import { TeamView as TeamViewType } from "@common/types/views/teamViews";
import { View } from "@common/types/views/views";
import { CommandIsland } from "@design-system/CommandIsland/CommandIsland";
import commandIslandStyles from "@design-system/CommandIsland/CommandIslandVariants.module.scss";
import { CommandIslandTotal } from "@design-system/CommandIsland/CommandIslandTotal";
import { pluralize } from "@common/helpers/string";
import {
    useCurrentOrganizationMembers,
    usePendingOrganizationInvites,
} from "@api/organizationMember.api";
import { sortBy } from "@common/helpers/collections";
import {
    organizationUserRolesInOrder,
    OrganizationUserRole,
} from "@common/userOrganizationRoles";
import { ORGANIZATION_ROLE_TO_OWNED_WORKSPACE_ROLE } from "@common/constants/organization";
import { teamViewConfig, TeamMemberRow } from "./teamViewConfig";
import styles from "./TeamView.module.scss";

interface TeamViewProps {
    view: TeamViewType;
    search?: string;
    onViewChange?: (view: TeamViewType) => void;
    onSearchClear?: () => void;
}

export const TeamView: FC<TeamViewProps> = ({
    view,
    search,
    onViewChange,
    onSearchClear,
}) => {
    const { data: members, isFetched: membersFetched } =
        useCurrentOrganizationMembers();
    const { data: pendingInvites, isFetched: invitesFetched } =
        usePendingOrganizationInvites();

    const rows: TeamMemberRow[] | null = useMemo(() => {
        if (!membersFetched || !invitesFetched) {
            return null;
        }

        let membersToDisplay = members ?? [];
        let invitesToDisplay = pendingInvites ?? [];

        if (search) {
            membersToDisplay = membersToDisplay.filter(
                (member) =>
                    member.user.name
                        .toLowerCase()
                        .includes(search.toLowerCase()) ||
                    member.user.email
                        ?.toLowerCase()
                        .includes(search.toLowerCase()),
            );

            invitesToDisplay = invitesToDisplay.filter((invite) =>
                invite.email?.toLowerCase().includes(search.toLowerCase()),
            );
        }

        const sortedMembers = sortBy(
            membersToDisplay,
            (member) => organizationUserRolesInOrder.indexOf(member.role),
            (member) => member.user.id,
        ).map((member) => ({
            ...member,
            type: "member" as const,
            id: member.user.id,
        }));

        const sortedInvites = sortBy(
            invitesToDisplay,
            (invite) =>
                organizationUserRolesInOrder.indexOf(invite.organizationRole!),
            (invite) => invite.email,
        ).map((invite) => ({
            ...invite,
            type: "invite" as const,
            inviteAssignments:
                invite.workspaceAssignments?.map((assignment) => ({
                    workspaceId: assignment.workspace.id,
                    workspaceName: assignment.workspace.name,
                    role: ORGANIZATION_ROLE_TO_OWNED_WORKSPACE_ROLE[
                        invite.organizationRole as OrganizationUserRole
                    ],
                })) ?? [],
        }));

        return [...sortedMembers, ...sortedInvites];
    }, [members, pendingInvites, search, membersFetched, invitesFetched]);

    const handleViewChange = (updatedView: View) => {
        onViewChange?.(updatedView as TeamViewType);
    };

    const totalCount = rows?.length ?? 0;

    return (
        <ViewTable
            data={rows}
            view={view}
            className={styles.teamTable}
            config={teamViewConfig}
            onViewChange={handleViewChange}
            enableColumnResizing
            fixedTopOffsetPx
            emptyView={() => {
                if (rows?.length === 0) {
                    return (
                        <EmptyState
                            header="No team members found"
                            action={onSearchClear}
                        />
                    );
                }
                return null;
            }}
            footer={
                totalCount > 0 ? (
                    <CommandIsland
                        sticky
                        className={commandIslandStyles.bulkActionsStickyFooter}
                    >
                        <CommandIslandTotal>
                            {totalCount} {pluralize("team member", totalCount)}
                        </CommandIslandTotal>
                    </CommandIsland>
                ) : undefined
            }
        />
    );
};
